import {useCallback, useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {SearchType} from '../../pages/search/types'
import {SEARCH_TYPE_STORAGE_KEY} from '../../pages/search/utils'
import {SEARCH_TYPE_META} from '../../../_metronic/layout/components/header/search/searchTypeMeta'

const SELECTABLE_TYPES = new Set<SearchType>(SEARCH_TYPE_META.map((m) => m.value))

/**
 * Header type selector: always starts at 'all' on mount and re-resets to 'all' on every SPA
 * route change. The user's manual selection is kept in-memory only, so navigating (or reloading)
 * clears it — PR-I-54 explicitly asked for the selector NOT to survive navigation.
 *
 * Legacy value cleanup: the old build persisted the selection under `nobilis.search.type`;
 * we proactively wipe that key on mount so returning users don't see stale state via any
 * future reader. Wrapped in try/catch: storage may be unavailable (private mode / blocked)
 * and it must never break the header.
 */
export function useSearchType(): {type: SearchType; setType: (t: SearchType) => void} {
  const [type, setTypeState] = useState<SearchType>('all')
  const location = useLocation()

  // One-time cleanup of the deprecated persisted value.
  useEffect(() => {
    try {
      localStorage.removeItem(SEARCH_TYPE_STORAGE_KEY)
    } catch {
      // Storage unavailable — nothing to clean, safe to ignore.
    }
  }, [])

  // Reset to 'all' on every SPA route change (pathname). We intentionally ignore search/hash
  // so typing in the header while on the same page does not clobber the user's selection.
  useEffect(() => {
    setTypeState('all')
  }, [location.pathname])

  const setType = useCallback((next: SearchType) => {
    // Guard against unknown values sneaking in from callers — fall back to 'all'.
    setTypeState(SELECTABLE_TYPES.has(next) ? next : 'all')
  }, [])

  return {type, setType}
}
